import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../axios';
import { toast } from 'react-toastify';
import {
  Container,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  TextField,
  Button,
  MenuItem,
  Grid,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import VoucherPrintDialog from './VoucherPrintDialog';
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

const VouchersListPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const companyId = parseInt(localStorage.getItem('companyId'), 10);
  
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [selectedVoucherForPrint, setSelectedVoucherForPrint] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(true);
  
  const [filters, setFilters] = useState({
    companyId,
    voucherType: '',
    paymentType: '',
    customerId: '',
    supplierId: '',
    fromDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    toDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    minAmount: '',
    maxAmount: '',
    searchText: ''
  });

  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  
  const voucherTypes = [
    { value: '', label: 'جميع الأنواع' },
    { value: 101, label: 'سند قبض من عميل' },
    { value: 102, label: 'سند صرف لمورد' },
    { value: 103, label: 'سند قبض عام' },
    { value: 104, label: 'سند صرف عام' },
    { value: 105, label: 'سند صرف ضريبة' }
  ];
  
  const paymentTypes = [
    { value: '', label: 'جميع طرق الدفع' },
    { value: 1, label: 'نقدي' },
    { value: 2, label: 'بنكي' }
  ];

  // نمط موحّد ومضغوط لأزرار الهيدر لتناسب جميع المقاسات
  const headerBtnSx = {
    borderRadius: 1.5,
    boxShadow: 2,
    flex: { xs: '1 1 calc(50% - 6px)', md: '0 0 auto' },
    minWidth: 0,
    px: { xs: 0.75, sm: 1.25, md: 1.5 },
    py: { xs: 0.5, md: 0.6 },
    fontSize: { xs: '0.68rem', sm: '0.75rem', md: '0.8rem' },
    fontWeight: 600,
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    '& .MuiButton-startIcon': {
      mr: { xs: 0.25, md: 0.5 },
      ml: { xs: 0.25, md: 0.5 },
      '& > *:nth-of-type(1)': { fontSize: { xs: '0.95rem', md: '1.05rem' } }
    }
  };

  // كرت ملخص مضغوط
  const summaryCardSx = {
    p: { xs: 1, sm: 1.25 },
    textAlign: 'center',
    color: 'white',
    borderRadius: 2,
    boxShadow: 2,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 0.25,
    minHeight: { xs: 58, sm: 64 },
    overflow: 'hidden'
  };

  // حقول الفلاتر المضغوطة
  const filterFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.5,
      fontSize: { xs: '0.8rem', md: '0.875rem' },
      '& input': {
        direction: 'ltr',
        textAlign: 'left',
        fontFamily: 'Arial, sans-serif'
      }
    },
    '& .MuiInputLabel-root': { fontSize: { xs: '0.8rem', md: '0.875rem' } }
  };

  // أزرار الفلاتر
  const filterBtnSx = {
    borderRadius: 1.5,
    px: { xs: 2, md: 3 },
    fontSize: { xs: '0.8rem', md: '0.875rem' },
    flex: { xs: '1 1 auto', sm: '0 0 auto' }
  };

  useEffect(() => {
    fetchVouchers();
    fetchSummary();
    fetchCustomers();
    fetchSuppliers();
    loadCompanyInfo();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchVouchers();
      fetchSummary();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  const fetchVouchers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          if (['voucherType', 'paymentType', 'customerId', 'supplierId', 'minAmount', 'maxAmount'].includes(key)) {
            params.append(key, Number(value));
          } else {
            params.append(key, value);
          }
        }
      });

      const response = await axios.get(`/Vouchers?${params.toString()}`);
      const vouchersData = response.data?.data || response.data?.vouchers || response.data || [];

      if (!Array.isArray(vouchersData)) {
        console.warn('⚠️ البيانات المستلمة ليست مصفوفة:', response.data);
        setVouchers([]);
        return;
      }

      const processedVouchers = vouchersData.map((voucher, index) => ({
        ...voucher,
        id: voucher.id || `temp-${index}`,
        voucherNumber: voucher.voucherNumber || 'غير محدد',
        voucherTypeName: voucher.voucherTypeName || 'غير محدد',
        amount: Number(voucher.amount) || 0
      }));

      setVouchers(processedVouchers);
    } catch (error) {
      console.error('خطأ في جلب السندات:', error);
      toast.error(`فشل في جلب السندات: ${error.response?.data?.message || error.message}`);
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const params = new URLSearchParams({
        companyId,
        fromDate: filters.fromDate,
        toDate: filters.toDate
      });
      const response = await axios.get(`/Vouchers/summary?${params.toString()}`);
      setSummary(response.data);
    } catch (error) {
      console.error('خطأ في جلب ملخص السندات:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`/Customer/by-company/${companyId}`);
      setCustomers(response.data || []);
    } catch (error) {
      console.error('خطأ في جلب العملاء:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`/Supplier/by-company/${companyId}`);
      setSuppliers(response.data || []);
    } catch (error) {
      console.error('خطأ في جلب الموردين:', error);
    }
  };

  const loadCompanyInfo = async () => {
    try {
      const response = await axios.get(`/CompanyProfile/public/${companyId}`);
      setCompanyInfo({
        name: response.data.nameAr,
        nameEn: response.data.nameEn,
        taxNumber: response.data.vatNo,
        address: response.data.address,
        phone: response.data.phone,
        email: response.data.email,
        logoUrl: response.data.logoUrl
      });
    } catch (error) {
      console.error('خطأ في تحميل معلومات الشركة:', error);
    }
  };

  const handleSearch = () => {
    fetchVouchers();
    fetchSummary();
  };

  const handleReset = () => {
    setFilters({
      companyId,
      voucherType: '',
      paymentType: '',
      customerId: '',
      supplierId: '',
      fromDate: dayjs().startOf('month').format('YYYY-MM-DD'),
      toDate: dayjs().endOf('month').format('YYYY-MM-DD'),
      minAmount: '',
      maxAmount: '',
      searchText: ''
    });
  };

  const handleFilterChange = (field, value) => {
    if (field === 'voucherType' && value !== '') {
      setFilters(prev => ({ ...prev, [field]: Number(value) }));
    } else {
      setFilters(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleDeleteVoucher = async (voucherId) => {
    try {
      await axios.delete(`/Vouchers/${voucherId}`);
      toast.success('تم حذف السند بنجاح');
      fetchVouchers();
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('خطأ في حذف السند:', error);
      toast.error('فشل في حذف السند');
    }
  };

  const handlePrintVoucher = (voucher) => {
    setSelectedVoucherForPrint(voucher);
    setPrintDialogOpen(true);
  };

  const handleViewVoucher = (voucher) => {
    switch (voucher.voucherType) {
      case 101: // Customer Receipt
        navigate(`/vouchers/customer-receipt/${voucher.id}/view`);
        break;
      case 102: // Supplier Payment
        navigate(`/vouchers/supplier-payment/${voucher.id}/view`);
        break;
      case 103: // General Receipt
        navigate(`/vouchers/general-receipt/${voucher.id}/view`);
        break;
      case 104: // General Payment
        navigate(`/vouchers/general-payment/${voucher.id}/view`);
        break;
      case 105: // Tax Payment
        navigate(`/vouchers/tax-payment/${voucher.id}/view`);
        break;
      default:
        toast.error('نوع السند غير معروف');
    }
  };

  const handleEditVoucher = (voucher) => {
    switch (voucher.voucherType) {
      case 101: // Customer Receipt
        navigate(`/vouchers/customer-receipt/${voucher.id}/edit`);
        break;
      case 102: // Supplier Payment
        navigate(`/vouchers/supplier-payment/${voucher.id}/edit`);
        break;
      case 103: // General Receipt
        navigate(`/vouchers/general-receipt/${voucher.id}/edit`);
        break;
      case 104: // General Payment
        navigate(`/vouchers/general-payment/${voucher.id}/edit`);
        break;
      case 105: // Tax Payment
        navigate(`/vouchers/tax-payment/${voucher.id}/edit`);
        break;
      default:
        toast.error('نوع السند غير معروف');
    }
  };

  const columns = [
    {
      field: 'voucherNumber',
      headerName: 'رقم السند',
      width: 105,
      renderCell: (params) => {
        if (!params || !params.value) return <span>-</span>;
        return (
          <Chip 
            label={params.value} 
            size="small" 
            color="primary" 
            variant="outlined"
          />
        );
      }
    },
    {
      field: 'voucherTypeName',
      headerName: 'نوع السند',
      width: 132,
      renderCell: (params) => {
        if (!params || !params.row) return <span>-</span>;
        const type = params.row.voucherType;
        const typeConfig = {
          101: { color: 'success', icon: <ReceiptIcon /> },
          102: { color: 'error', icon: <PaymentIcon /> },
          103: { color: 'primary', icon: <ReceiptIcon /> },
          104: { color: 'warning', icon: <PaymentIcon /> },
          105: { color: 'warning', icon: <PaymentIcon /> },
        };
        const config = typeConfig[type] || { color: 'default', icon: null };
        return (
          <Chip 
            label={params.value || 'غير محدد'}
            size="small"
            color={config.color}
            icon={config.icon}
          />
        );
      }
    },
    {
      field: 'voucherDate',
      headerName: 'التاريخ',
      width: 118,
      renderCell: (params) => {
        if (!params || !params.value) return <span>-</span>;
        try {
          const date = dayjs(params.value);
          if (!date.isValid()) return <span>-</span>;
          return (
            <Tooltip title={date.format('YYYY/MM/DD HH:mm:ss')}>
              <Chip
                label={date.format('YYYY/MM/DD')}
                size="small"
                variant="outlined"
                color="default"
                sx={{
                  fontFamily: 'Arial, sans-serif',
                  direction: 'ltr',
                  '& .MuiChip-label': { px: 0.75, fontSize: '0.72rem' }
                }}
              />
            </Tooltip>
          );
        } catch {
          return <span>-</span>;
        }
      }
    },
    {
      field: 'customerName',
      headerName: 'العميل/المورد',
      width: 158,
      renderCell: (params) => {
        if (!params || !params.row) return <span>عام</span>;
        const name = params.row.customerName || params.row.supplierName;
        if (!name) return <span style={{ color: '#666' }}>عام</span>;
        const isCustomer = !!params.row.customerName;
        return (
          <Chip
            label={name}
            size="small"
            color={isCustomer ? 'info' : 'warning'}
            variant="outlined"
          />
        );
      }
    },
    {
      field: 'paymentTypeName',
      headerName: 'طريقة الدفع',
      width: 108,
      renderCell: (params) => {
        if (!params || !params.row) return <span>غير محدد</span>;
        const paymentType = params.row.paymentType;
        const paymentTypeName = params.row.paymentTypeName || 
          (paymentType === 1 ? 'نقدي' : paymentType === 2 ? 'بنكي' : 'غير محدد');
        return (
          <Chip
            label={paymentTypeName}
            size="small"
            color={paymentType === 1 ? 'success' : paymentType === 2 ? 'primary' : 'default'}
            variant="outlined"
          />
        );
      }
    },
    {
      field: 'amount',
      headerName: 'المبلغ',
      width: 140,
      type: 'number',
      renderCell: (params) => {
        const amount = params?.value || 0;
        const isReceipt = params?.row?.voucherType === 101 || params?.row?.voucherType === 103;
        return (
          <Chip
            label={new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(amount) + ' ريال'}
            size="small"
            color={isReceipt ? 'success' : 'error'}
            variant="outlined"
            sx={{
              fontFamily: 'Arial, sans-serif',
              direction: 'ltr',
              '& .MuiChip-label': { px: 0.75, fontSize: '0.72rem' }
            }}
          />
        );
      }
    },
    {
      field: 'branchName',
      headerName: 'الفرع',
      width: 118,
      renderCell: (params) => {
        const name = params?.value || '-';
        return (
          <Chip
            label={name}
            size="small"
            variant="outlined"
            sx={{ '& .MuiChip-label': { px: 0.75, fontSize: '0.72rem' } }}
          />
        );
      }
    },
    {
      field: 'description',
      headerName: 'الوصف',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const value = params?.value || params?.row?.notes || '';
        return (
          <Tooltip title={value}>
            <span>{value || '-'}</span>
          </Tooltip>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'الإجراءات',
      width: 168,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        const type = params.row.voucherType;
        const isReceipt = type === 101 || type === 103;
        const isPayment = type === 102 || type === 104 || type === 105;
        
        const canView = isReceipt 
          ? hasPermission(PERMISSIONS.BOND_RECEIPT_VIEW) 
          : (isPayment ? hasPermission(PERMISSIONS.BOND_PAYMENT_VIEW) : hasPermission(PERMISSIONS.VOUCHER_VIEW));
        const canEdit = isReceipt 
          ? hasPermission(PERMISSIONS.BOND_RECEIPT_EDIT) 
          : (isPayment ? hasPermission(PERMISSIONS.BOND_PAYMENT_EDIT) : false);
        const canPrint = isReceipt 
          ? hasPermission(PERMISSIONS.BOND_RECEIPT_PRINT) 
          : (isPayment ? hasPermission(PERMISSIONS.BOND_PAYMENT_PRINT) : false);
        const canDelete = isReceipt 
          ? hasPermission(PERMISSIONS.BOND_RECEIPT_DELETE) 
          : (isPayment ? hasPermission(PERMISSIONS.BOND_PAYMENT_DELETE) : false);

        return (
          <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center' }}>
             {/* ✅ View Journal Entry Button */}
            {params.row.journalEntryGlobalId && (
              <Tooltip title="عرض القيد">
                <IconButton 
                  size="small" 
                  sx={{ color: '#9c27b0' }} 
                  onClick={() => window.open(`/journal-entries/${params.row.journalEntryGlobalId}/view`, "_blank")}
                >
                  <ViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {canView && (
              <Tooltip title="عرض">
                <IconButton size="small" color="info" onClick={() => handleViewVoucher(params.row)}>
                  <ViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canEdit && (
              <Tooltip title="تعديل">
                <IconButton size="small" color="primary" onClick={() => handleEditVoucher(params.row)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canPrint && (
              <Tooltip title="طباعة">
                <IconButton size="small" color="secondary" onClick={() => handlePrintVoucher(params.row)}>
                  <PrintIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="حذف">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => {
                    setSelectedVoucher(params.row);
                    setDeleteDialogOpen(true);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      }
    }
  ];

  return (
    <Container
      maxWidth={false}
      sx={{
        mt: { xs: 1, sm: 1.5, md: 2 },
        px: { xs: 1, sm: 1.5, md: 2.5 },
        pb: { xs: 2, sm: 3, md: 4 }
      }}
      dir="rtl"
    >
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          background: 'linear-gradient(135deg, #00897b 0%, #00695c 100%)',
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          mb: { xs: 2, sm: 2.5, md: 3 },
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: { xs: 1.25, sm: 1.75, md: 2 } }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              rowGap: { xs: 1.25, md: 1 },
              columnGap: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1.5 }, minWidth: 0 }}>
              <AccountBalanceIcon sx={{ fontSize: { xs: 26, sm: 30, md: 34 }, color: 'white', flexShrink: 0 }} />
              <Typography
                fontWeight="bold"
                color="white"
                sx={{
                  fontSize: { xs: '1.05rem', sm: '1.25rem', md: '1.45rem' },
                  lineHeight: 1.3,
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
              >
                💰 إدارة السندات المالية
              </Typography>
            </Box>
            <Stack
              direction="row"
              flexWrap="wrap"
              useFlexGap
              spacing={{ xs: 0.75, md: 1 }}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              {hasPermission(PERMISSIONS.BOND_RECEIPT_CREATE) && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/vouchers/customer-receipt')}
                  sx={{ ...headerBtnSx, bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
                >
                  سند قبض عميل
                </Button>
              )}
              {hasPermission(PERMISSIONS.BOND_PAYMENT_CREATE) && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/vouchers/supplier-payment')}
                  sx={{ ...headerBtnSx, bgcolor: 'error.main', '&:hover': { bgcolor: 'error.dark' } }}
                >
                  سند صرف مورد
                </Button>
              )}
              {hasPermission(PERMISSIONS.BOND_RECEIPT_CREATE) && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/vouchers/general-receipt')}
                  sx={{ ...headerBtnSx, bgcolor: 'info.main', '&:hover': { bgcolor: 'info.dark' } }}
                >
                  سند قبض عام
                </Button>
              )}
              {hasPermission(PERMISSIONS.BOND_PAYMENT_CREATE) && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/vouchers/general-payment')}
                  sx={{ ...headerBtnSx, bgcolor: 'secondary.main', '&:hover': { bgcolor: 'secondary.dark' } }}
                >
                  سند صرف عام
                </Button>
              )}
              {hasPermission(PERMISSIONS.BOND_TAX_CREATE) && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/vouchers/tax-payment')}
                  sx={{ ...headerBtnSx, bgcolor: 'warning.main', '&:hover': { bgcolor: 'warning.dark' } }}
                >
                  سند ضريبة
                </Button>
              )}
            </Stack>
          </Box>
        </Box>
      </Paper>

      {/* ملخص السندات */}
      {summary && (
        <Grid container spacing={{ xs: 1, sm: 1.5 }} mb={{ xs: 1.5, md: 2 }}>
          <Grid item xs={6} md={3}>
            <Paper sx={{ ...summaryCardSx, background: 'linear-gradient(135deg, #66bb6a 0%, #43a047 100%)' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2, opacity: 0.95 }}>
                إجمالي المقبوضات
              </Typography>
              <Typography sx={{ fontFamily: 'Arial, sans-serif', fontWeight: 'bold', fontSize: { xs: '0.95rem', sm: '1.15rem', md: '1.3rem' }, lineHeight: 1.3, direction: 'ltr' }}>
                {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(summary.totalReceipts || 0)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ ...summaryCardSx, background: 'linear-gradient(135deg, #ef5350 0%, #d32f2f 100%)' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2, opacity: 0.95 }}>
                إجمالي المدفوعات
              </Typography>
              <Typography sx={{ fontFamily: 'Arial, sans-serif', fontWeight: 'bold', fontSize: { xs: '0.95rem', sm: '1.15rem', md: '1.3rem' }, lineHeight: 1.3, direction: 'ltr' }}>
                {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(summary.totalPayments || 0)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ ...summaryCardSx, background: 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2, opacity: 0.95 }}>
                صافي التدفق النقدي
              </Typography>
              <Typography sx={{ fontFamily: 'Arial, sans-serif', fontWeight: 'bold', fontSize: { xs: '0.95rem', sm: '1.15rem', md: '1.3rem' }, lineHeight: 1.3, direction: 'ltr' }}>
                {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(summary.netCashFlow || 0)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ ...summaryCardSx, background: 'linear-gradient(135deg, #ffa726 0%, #fb8c00 100%)' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2, opacity: 0.95 }}>
                الفترة
              </Typography>
              <Typography sx={{ fontFamily: 'Arial, sans-serif', fontWeight: 'bold', fontSize: { xs: '0.72rem', sm: '0.85rem', md: '0.95rem' }, lineHeight: 1.4, direction: 'ltr' }}>
                {dayjs(filters.fromDate).format('YYYY/MM/DD')} - {dayjs(filters.toDate).format('YYYY/MM/DD')}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* فلاتر البحث */}
      <Card sx={{ mb: { xs: 1.5, md: 2 }, borderRadius: 2, boxShadow: 2 }}>
        <CardHeader
          title="فلاتر البحث"
          titleTypographyProps={{ fontWeight: 'bold', fontSize: { xs: '0.95rem', md: '1.1rem' } }}
          avatar={<FilterIcon fontSize="small" />}
          action={
            <IconButton size="small" onClick={() => setFiltersOpen(!filtersOpen)}>
              {filtersOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          }
          sx={{
            bgcolor: 'grey.100',
            py: { xs: 0.75, md: 1 },
            px: { xs: 1.5, md: 2 }
          }}
        />
        <Collapse in={filtersOpen}>
          <CardContent sx={{ p: { xs: 1.25, sm: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.25, md: 2 } } }}>
            <Grid container spacing={{ xs: 1, sm: 1.5 }}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="نوع السند"
                  value={filters.voucherType}
                  onChange={(e) => handleFilterChange('voucherType', e.target.value)}
                  sx={filterFieldSx}
                >
                  {voucherTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="طريقة الدفع"
                  value={filters.paymentType}
                  onChange={(e) => handleFilterChange('paymentType', e.target.value)}
                  sx={filterFieldSx}
                >
                  {paymentTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="العميل"
                  value={filters.customerId}
                  onChange={(e) => handleFilterChange('customerId', e.target.value)}
                  sx={filterFieldSx}
                >
                  <MenuItem value="">جميع العملاء</MenuItem>
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  size="small"
                  label="المورد"
                  value={filters.supplierId}
                  onChange={(e) => handleFilterChange('supplierId', e.target.value)}
                  sx={filterFieldSx}
                >
                  <MenuItem value="">جميع الموردين</MenuItem>
                  {suppliers.map((supplier) => (
                    <MenuItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  size="small"
                  label="من تاريخ"
                  value={filters.fromDate}
                  onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ lang: 'en', dir: 'ltr' }}
                  sx={filterFieldSx}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  size="small"
                  label="إلى تاريخ"
                  value={filters.toDate}
                  onChange={(e) => handleFilterChange('toDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ lang: 'en', dir: 'ltr' }}
                  sx={filterFieldSx}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  size="small"
                  label="أقل مبلغ"
                  value={filters.minAmount}
                  onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                  inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
                  sx={filterFieldSx}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  size="small"
                  label="أكبر مبلغ"
                  value={filters.maxAmount}
                  onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                  inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
                  sx={filterFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="البحث في النصوص"
                  value={filters.searchText}
                  onChange={(e) => handleFilterChange('searchText', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                  sx={filterFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  useFlexGap
                  spacing={{ xs: 1, md: 1.5 }}
                  justifyContent="center"
                >
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    sx={filterBtnSx}
                  >
                    بحث
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleReset}
                    sx={filterBtnSx}
                  >
                    إعادة تعيين
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      {/* جدول السندات */}
      <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
        <CardHeader
          title={`قائمة السندات (${vouchers.length})`}
          titleTypographyProps={{ fontWeight: 'bold', fontSize: { xs: '0.95rem', md: '1.1rem' } }}
          sx={{
            bgcolor: 'grey.100',
            py: { xs: 0.75, md: 1 },
            px: { xs: 1.5, md: 2 }
          }}
        />
        <CardContent sx={{ p: { xs: 0.5, sm: 1, md: 1.5 }, '&:last-child': { pb: { xs: 0.5, md: 1.5 } } }}>
          <Box sx={{ height: { xs: '58vh', sm: '62vh', md: '64vh' }, minHeight: 380, width: '100%' }}>
            <DataGrid
              rows={vouchers}
              columns={columns}
              loading={loading}
              density="compact"
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25 }
                }
              }}
              pageSizeOptions={[25, 50, 100]}
              disableRowSelectionOnClick
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 300 }
                }
              }}
              localeText={{
                noRowsLabel: 'لا توجد سجلات',
                toolbarExport: 'تصدير',
                toolbarColumns: 'الأعمدة',
                toolbarFilters: 'الفلاتر',
                toolbarQuickFilterPlaceholder: 'بحث سريع...',
              }}
              sx={{
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f5f5f5',
                  fontWeight: 'bold'
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold',
                  fontSize: { xs: '0.75rem', md: '0.82rem' }
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #e0e0e0',
                  fontSize: { xs: '0.75rem', md: '0.82rem' },
                  display: 'flex',
                  alignItems: 'center'
                },
                '& .MuiDataGrid-toolbarContainer': {
                  padding: { xs: '6px', md: '10px' },
                  flexWrap: 'wrap',
                  rowGap: 0.5
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#f8f9fa'
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* مربع حوار تأكيد الحذف */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <Typography>هل أنت متأكد من حذف السند رقم {selectedVoucher?.voucherNumber}؟</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            لا يمكن التراجع عن هذا الإجراء.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>إلغاء</Button>
          <Button onClick={() => handleDeleteVoucher(selectedVoucher?.id)} color="error" variant="contained">
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* مربع حوار الطباعة */}
      <VoucherPrintDialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        voucher={selectedVoucherForPrint}
        companyInfo={companyInfo}
      />
    </Container>
  );
};

export default VouchersListPage;